import { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Checkbox, message, Tag, DatePicker, Drawer, Tabs, Descriptions } from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  supabase,
  provisionAuthUser,
  deleteFleetOperator,
  formatDateTime,
  formatDate,
  useAuthUser,
  parseUserRole,
  isFleetOwnerRole,
  usePortalPermissions,
  resetPasswordRedirectUrl,
  SetupCredentialsNotice,
} from '@ubus/shared';
import type { FleetOperator } from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import StatusTag from '../components/StatusTag';
import { TableRowActions } from '../components/TableRowActions';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import ForbiddenPage from './ForbiddenPage';
import FleetOperatorMembersPanel from './FleetOperatorMembersPanel';
import { useTranslation } from 'react-i18next';
import { localeConfirm, localeSuccess } from '../utils/localeModal';
import { formatAdminDbError } from '../utils/formatAdminDbError';

function FleetOperators() {
  const { t, i18n } = useTranslation();
  const user = useAuthUser();
  const { hasPermission } = usePortalPermissions();
  const canCreate = hasPermission('fleet_operators.create');
  const canEdit = hasPermission('fleet_operators.edit');
  const canProvision = hasPermission('fleet_operators.provision');
  const canViewMembers =
    hasPermission('fleet_operators.members.view') ||
    hasPermission('fleet_operators.view');
  const [operators, setOperators] = useState<FleetOperator[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<FleetOperator | null>(null);
  const [detailOperator, setDetailOperator] = useState<FleetOperator | null>(null);
  const [form] = Form.useForm();
  const forbidden = isFleetOwnerRole(parseUserRole(user));
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const fetchOperators = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('fleet_operators')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        message.error(error.message);
      } else {
        setOperators(data ?? []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchOperators();
  }, []);

  const handleCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' });
    setModalVisible(true);
  };

  const handleEdit = (record: FleetOperator) => {
    setEditing(record);
    form.setFieldsValue({
      ...record,
      comprehensive_insurance_expiry: record.comprehensive_insurance_expiry
        ? dayjs(record.comprehensive_insurance_expiry)
        : null,
      public_liability_expiry: record.public_liability_expiry
        ? dayjs(record.public_liability_expiry)
        : null,
    });
    setModalVisible(true);
  };

  const handleOpenDetail = (record: FleetOperator) => {
    setDetailOperator(record);
  };

  const handleDetailOperatorChanged = async () => {
    await fetchOperators();
    if (!detailOperator) return;
    const { data } = await supabase
      .from('fleet_operators')
      .select('*')
      .eq('id', detailOperator.id)
      .maybeSingle();
    if (data) setDetailOperator(data as FleetOperator);
  };

  const handleDelete = (id: string) => {
    localeConfirm(t, {
      title: t('fleetOperators.messages.deleteTitle'),
      content: t('fleetOperators.messages.deleteContent'),
      okText: t('fleetOperators.actions.delete'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteFleetOperator(id);
          message.success(t('fleetOperators.messages.deleted'));
          void fetchOperators();
        } catch (err) {
          message.error(formatAdminDbError(err, i18n.language, 'delete'));
          return Promise.reject(err);
        }
      },
    });
  };

  const handleProvisionLogin = async (record: FleetOperator) => {
    localeConfirm(t, {
      title: t('fleetOperators.messages.provisionTitle'),
      content: t('fleetOperators.messages.provisionContent', { email: record.contact_email }),
      okText: t('fleetOperators.messages.provision'),
      onOk: async () => {
        try {
          const auth = await provisionAuthUser({
            role: 'fleet_owner',
            email: record.contact_email,
            fleetOperatorId: record.id,
            redirectTo: resetPasswordRedirectUrl(),
          });
          localeSuccess(t, {
            title: t('fleetOperators.messages.accountReady'),
            width: 560,
            content: (
              <SetupCredentialsNotice email={auth.email} setupLink={auth.setupLink} />
            ),
          });
          void fetchOperators();
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('fleetOperators.messages.provisionFailed'));
        }
      },
    });
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      name: values.name,
      contact_person: values.contact_person,
      contact_phone: values.contact_phone,
      contact_email: values.contact_email.trim().toLowerCase(),
      address: values.address || null,
      status: values.status as FleetOperator['status'],
      comprehensive_insurance_expiry: values.comprehensive_insurance_expiry
        ? dayjs(values.comprehensive_insurance_expiry).format('YYYY-MM-DD')
        : null,
      public_liability_expiry: values.public_liability_expiry
        ? dayjs(values.public_liability_expiry).format('YYYY-MM-DD')
        : null,
      updated_at: new Date().toISOString(),
    };

    let mutatedId: string | undefined;
    if (editing) {
      mutatedId = editing.id;
      const { error } = await supabase
        .from('fleet_operators')
        .update(payload)
        .eq('id', editing.id);

      if (error) {
        message.error(error.message);
        return;
      }
      message.success(t('fleetOperators.messages.updated'));
    } else {
      const { data, error } = await supabase
        .from('fleet_operators')
        .insert({ ...payload, created_at: new Date().toISOString() })
        .select('id')
        .single();

      if (error) {
        message.error(error.message);
        return;
      }
      mutatedId = data?.id;

      if (values.create_login && data?.id) {
        try {
          const auth = await provisionAuthUser({
            role: 'fleet_owner',
            email: payload.contact_email,
            fleetOperatorId: data.id,
            redirectTo: resetPasswordRedirectUrl(),
          });
          localeSuccess(t, {
            title: t('fleetOperators.messages.created'),
            width: 560,
            content: (
              <SetupCredentialsNotice email={auth.email} setupLink={auth.setupLink} />
            ),
          });
        } catch (err) {
          message.warning(
            err instanceof Error ? err.message : t('fleetOperators.messages.loginProvisionFailed')
          );
        }
      } else {
        message.success(t('fleetOperators.messages.created'));
      }
    }

    setModalVisible(false);
    await afterMutation(mutatedId, fetchOperators);
  };

  const columns: TableProps<FleetOperator>['columns'] = [
    {
      title: t('fleetOperators.columns.name'),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
    },
    {
      title: t('fleetOperators.columns.contact'),
      dataIndex: 'contact_person',
      key: 'contact_person',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('fleetOperators.columns.phone'),
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      width: 140,
    },
    {
      title: t('fleetOperators.columns.email'),
      dataIndex: 'contact_email',
      key: 'contact_email',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('fleetOperators.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: FleetOperator['status']) => <StatusTag status={status} />,
    },
    {
      title: t('fleetOperators.columns.login'),
      key: 'login',
      width: 120,
      render: (_, record) =>
        record.auth_uid ? (
          <Tag color="blue">Linked</Tag>
        ) : canProvision ? (
          <Button type="link" size="small" icon={<KeyOutlined />} onClick={() => handleProvisionLogin(record)}>
            Provision
          </Button>
        ) : (
          <Tag>—</Tag>
        ),
    },
    {
      title: t('fleetOperators.columns.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: t('fleetOperators.columns.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (value: string) => formatDateTime(value),
      sorter: (a, b) => a.updated_at.localeCompare(b.updated_at),
    },
    {
      title: t('fleetOperators.columns.actions'),
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <TableRowActions>
          {canViewMembers ? (
            <Button
              type="link"
              icon={<TeamOutlined />}
              onClick={() => handleOpenDetail(record)}
            >
              {t('fleetOperators.actions.members')}
            </Button>
          ) : null}
          {canEdit ? (
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              Edit
            </Button>
          ) : null}
          {canEdit ? (
            <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
              Delete
            </Button>
          ) : null}
        </TableRowActions>
      ),
    },
  ];

  if (forbidden) {
    return <ForbiddenPage />;
  }

  return (
    <AdminListPage
      title={t('fleetOperators.title')}
      subtitle={t('fleetOperators.subtitle')}
      extra={
        canCreate ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add operator
          </Button>
        ) : undefined
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={operators}
        pagination={pagination}
        onChange={onTableChange}
        rowClassName={rowClassName}
        scroll={{ x: 1300 }}
        locale={{
          emptyText: t('fleetOperators.empty'),
        }}
      />

      <Modal
        title={editing ? t('fleetOperators.edit') : t('fleetOperators.new')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => void handleSubmit()}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t('fleetOperators.fields.companyName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contact_person" label={t('fleetOperators.fields.contactPerson')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contact_phone" label={t('fleetOperators.fields.phone')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="contact_email"
            label={t('fleetOperators.fields.emailLogin')}
            rules={[{ required: true, type: 'email' }]}
          >
            <Input disabled={Boolean(editing?.auth_uid)} />
          </Form.Item>
          <Form.Item name="address" label={t('fleetOperators.fields.address')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="comprehensive_insurance_expiry"
            label={t('fleetOperators.fields.comprehensiveInsuranceExpiry')}
          >
            <DatePicker style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item
            name="public_liability_expiry"
            label={t('fleetOperators.fields.publicLiabilityExpiry')}
          >
            <DatePicker style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item name="status" label={t('fleetOperators.fields.status')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'active', label: t('fleetOperators.status.active') },
                { value: 'inactive', label: t('fleetOperators.status.inactive') },
              ]}
            />
          </Form.Item>
          {!editing && canProvision ? (
            <Form.Item name="create_login" valuePropName="checked" initialValue={false}>
              <Checkbox>{t('fleetOperators.fields.createLogin')}</Checkbox>
            </Form.Item>
          ) : null}
        </Form>
      </Modal>

      <Drawer
        title={detailOperator?.name ?? ''}
        open={Boolean(detailOperator)}
        onClose={() => setDetailOperator(null)}
        width={720}
        destroyOnClose
      >
        {detailOperator ? (
          <Tabs
            defaultActiveKey="members"
            items={[
              {
                key: 'details',
                label: t('fleetOperators.tabs.details'),
                children: (
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label={t('fleetOperators.columns.name')}>
                      {detailOperator.name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('fleetOperators.columns.contact')}>
                      {detailOperator.contact_person}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('fleetOperators.columns.phone')}>
                      {detailOperator.contact_phone}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('fleetOperators.columns.email')}>
                      {detailOperator.contact_email}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('fleetOperators.columns.status')}>
                      <StatusTag status={detailOperator.status} />
                    </Descriptions.Item>
                    <Descriptions.Item label={t('fleetOperators.columns.login')}>
                      {detailOperator.auth_uid ? (
                        <Tag color="blue">{t('fleetOperators.actions.linked')}</Tag>
                      ) : (
                        <Tag>—</Tag>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: 'members',
                label: t('fleetOperators.tabs.members'),
                children: (
                  <FleetOperatorMembersPanel
                    operator={detailOperator}
                    onOperatorChanged={handleDetailOperatorChanged}
                  />
                ),
              },
            ]}
          />
        ) : null}
      </Drawer>
    </AdminListPage>
  );
}

export default FleetOperators;
